import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, ToggleButtonGroup, ToggleButton, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { createInvitationService, IInvitationService } from '../../services/InvitationService';
import CalendarModal from '../Invitation/Calendar/calendarModal';
import { CalendarEvent } from 'calendar-link';
import i18next from 'i18next';

// Date utility functions (same as EditEvent page DateEvent component)
function formatDateUTC(dateString: string, locale: string = 'en-GB') {
    if (!dateString) return ""
    let clean = dateString.replace(/\.\d{6}Z$/, "Z")
    const date = new Date(clean)
    if (isNaN(date.getTime())) return dateString
    const formatter = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
    })
    const parts = formatter.formatToParts(date)
    const y = parts.find(p => p.type === 'year')?.value
    const m = parts.find(p => p.type === 'month')?.value
    const d = parts.find(p => p.type === 'day')?.value
    const h = parts.find(p => p.type === 'hour')?.value
    const min = parts.find(p => p.type === 'minute')?.value
    return `${y}-${m}-${d} ${h}:${min}`
}

function utcStringToLocalDate(utcString: string): Date | null {
    if (!utcString) return null
    let clean = utcString.replace(/\.\d{6}Z$/, "Z")
    const date = new Date(clean)
    if (isNaN(date.getTime())) return null
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000)
}

// Utility function to get event title based on language
function getEventTitle(eventData: any, language: string): string {
    if (language === 'ar') {
        // Use title_ar if available, otherwise use hardcoded Arabic default
        return eventData?.title_ar || 'فــــــــــــصــــــلٌ جــــــــــــديـــــــــد يبـــدأ هنا';
    }
    
    // For English, use name field or fallback to hardcoded English default
    return eventData?.name || 'RIYADH FASHION WEEK';
}

const RfwInvite = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const action = searchParams.get('action');
    const { t, i18n, ready } = useTranslation();

    // State for invitation data
    const [invitationData, setInvitationData] = useState<any>(null);
    const [viewModel, setViewModel] = useState<{
        guestName: string;
        dateStr: string;
        timeStr: string;
        locationStr: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // API base URL and service initialization (Dependency Inversion)
    const BASE_URL = import.meta.env.VITE_REACT_APP_API_BASE_URL;
    const invitationService: IInvitationService = React.useMemo(
        () => createInvitationService(BASE_URL),
        [BASE_URL]
    );

    // Initialize language state from i18n or localStorage
    const [lang, setLang] = React.useState<string>(() => {
        const storedLang = localStorage.getItem('i18nextLng');
        if (storedLang === 'ar' || storedLang === 'en') return storedLang;
        return i18n.language?.startsWith('ar') ? 'ar' : 'en';
    });

    // Force re-render when language changes
    const [, forceUpdate] = React.useReducer(x => x + 1, 0);

    // Button submission state (Single Responsibility: track submission status)
    type SubmissionState = 'idle' | 'accepting' | 'declining';
    const [submissionState, setSubmissionState] = React.useState<SubmissionState>('idle');

    // Wallet and Calendar state
    const [walletLoading, setWalletLoading] = useState(false);
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);

    // QR code (align with Invitation page: fetch server-generated QR and show as base64)
    const [eventQrcode, setEventQrcode] = useState<string | null>(null);
    const [qrcodeLoading, setQrcodeLoading] = useState<boolean>(false);

    // Fetch invitation data
    const fetchInvitationData = async () => {
        if (!id) {
            setError('No invitation ID provided');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const response = await axios.get(`${BASE_URL}/events/invitation/${id}`);
            console.log('[RFW Invite] GET /events/invitation/:id response:', response.data);
            console.log('[RFW Invite] Response status:', response.status);
            console.log('[RFW Invite] Full response object:', response);

            if (response.status === 200) {
                console.log('[RFW Invite] Setting invitation data:', response.data);

                // Check if response.data is empty or undefined
                if (!response.data || Object.keys(response.data).length === 0) {
                    console.warn('[RFW Invite] Response data is empty or undefined!');
                    setError('No invitation data received from server');
                    return;
                }

                setInvitationData(response.data);
                // Map API response to page view-model
                try {
                    const api = response.data;
                    const contact = api?.data?.contact || {};
                    const event0 = api?.data?.event?.data?.[0] || {};

                    // Use start_datetime for date and time (same approach as EditEvent page)
                    const startDatetime = event0?.start_datetime as string | undefined;
                    console.log('[RFW Invite] start_datetime from API:', startDatetime);

                    let dateStr = '';
                    let timeStr = '';

                    if (startDatetime) {
                        // Use the same UTC-preserving conversion as EditEvent page
                        const d = utcStringToLocalDate(startDatetime);
                        if (d && !isNaN(d.getTime())) {
                            // Format: "OCTOBER 2, 2025 - 02:00 AM" (in UTC)
                            dateStr = d.toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                            }).toUpperCase();
                            timeStr = d.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                            }).toUpperCase();
                        }
                    }

                    // Build location string from venue and address
                    const venuePart = event0?.venue || '';
                    const addressPart = event0?.address?.address || '';
                    const locationStr = [venuePart, addressPart].filter(Boolean).join(', ');

                    console.log('[RFW Invite] Mapped view model:', {
                        guestName: contact?.first_name,
                        dateStr,
                        timeStr,
                        locationStr
                    });

                    setViewModel({
                        guestName: [contact?.first_name, contact?.last_name].filter(Boolean).join(' ') || 'Guest',
                        dateStr: dateStr || '',
                        timeStr: timeStr || '',
                        locationStr: locationStr || ''
                    });
                } catch (mapErr) {
                    console.warn('[RFW Invite] Failed to map API response to view-model:', mapErr);
                }
            } else {
                setError('Failed to fetch invitation data');
            }
        } catch (error: any) {
            console.error('Error fetching invitation:', error);
            if (error?.response) {
                console.log('[RFW Invite] Error response data:', error.response.data);
                console.log('[RFW Invite] Error status:', error.response.status);
            }
            if (error.response?.status === 422) {
                setError('Invitation not found');
            } else if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An unexpected error occurred');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch data on component mount AND whenever URL action parameter changes
    // This ensures we always have fresh data from the server
    useEffect(() => {
        console.log('[RFW Invite] Component mounted or URL changed, id:', id, 'action:', action);
        console.log('[RFW Invite] BASE_URL:', BASE_URL);
        fetchInvitationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, action]);

    // Fetch QR code from backend using same endpoint/approach as main invitation page
    const fetchEventQrcode = useCallback(async () => {
        try {
            // Use the same identifier as Invitation page (hash from route param)
            if (!id) return;
            setQrcodeLoading(true);
            const response = await axios.get(
                `${BASE_URL}/events/qrcode/${id}`,
                { responseType: 'arraybuffer' }
            );
            const base64Image = btoa(
                new Uint8Array(response.data)
                    .reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            setEventQrcode(base64Image);
        } catch (e) {
            // leave as null; UI will handle fallback text
            setEventQrcode(null);
        } finally {
            setQrcodeLoading(false);
        }
    }, [id, BASE_URL]);

    // ----- State Model -----
    type InvitePageState = 'loading' | 'accept' | 'policy' | 'invitation' | 'error' | 'thankyou';
    const [pageState, setPageState] = React.useState<InvitePageState>('loading');

    // Expose simple transition helpers (Single Responsibility: state transitions)
    const goToAccept = () => setPageState('accept');
    const goToInvitation = () => setPageState('invitation');
    const goToPolicy = () => setPageState('policy');
    const goToError = () => setPageState('error');
    const goToThankYou = () => setPageState('thankyou');
    const goToLoading = () => setPageState('loading');

    // Build a robust maps URL that accepts direct links (maps.app.goo.gl or full Google Maps links),
    // coordinates, or a plain address string
    const buildMapsUrl = (venue?: string | null, addressText?: string | null, lat?: number | null, lng?: number | null) => {
        // Prefer coordinates when available
        if (typeof lat === 'number' && typeof lng === 'number' && isFinite(lat) && isFinite(lng) && (lat !== 0 || lng !== 0)) {
            return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        }

        // Combine and sanitize any pasted link
        const combinedRaw = `${venue || ''} ${addressText || ''}`.trim();
        const combined = combinedRaw.replace(/^\s*["'\(]+|["'\)]+\s*$/g, '');

        // Try to extract a direct link (allow missing scheme, and strip trailing quotes/parentheses)
        const directUrlRegex = /(https?:\/\/)?(?:maps\.app\.goo\.gl|goo\.gl\/maps|www\.google\.com\/maps|maps\.google\.com)[^\s\)"]+/i;
        const direct = combined.match(directUrlRegex)?.[0];
        if (direct) {
            const normalized = direct.startsWith('http') ? direct : `https://${direct}`;
            return normalized;
        }

        // Fallback to a Maps search query using the combined string
        if (combined) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(combined)}`;
        }

        return '';
    };

    // Derive page state from fetched invitation status and URL params
    useEffect(() => {
        console.log('[RFW Invite] State derivation - isLoading:', isLoading, 'error:', error, 'invitationData:', !!invitationData);

        // Stay in loading state while fetching
        if (isLoading) {
            setPageState('loading');
            return;
        }

        // Show error state if API call failed
        if (error) {
            setPageState('error');
            return;
        }

        // Wait for invitation data
        if (!invitationData) {
            console.log('[RFW Invite] No invitation data yet, staying in current state');
            return;
        }

        // Try to normalize various possible API status fields
        const rawStatus = (
            invitationData?.status ??
            invitationData?.invitation?.status ??
            invitationData?.currentStatus ??
            invitationData?.response ??
            invitationData?.rsvpStatus ??
            invitationData?.data?.status ??
            invitationData?.data?.currentStatus
        );

        console.log('[RFW Invite] Raw status value:', rawStatus, 'type:', typeof rawStatus);

        // Status: 1 = accepted, 2 = declined, 4 = scanned (treated as accepted)
        const accepted = Boolean(
            rawStatus === 1 ||
            rawStatus === '1' ||
            rawStatus === 4 ||
            rawStatus === '4' ||
            invitationData?.accepted === true ||
            invitationData?.isAccepted === true ||
            (typeof rawStatus === 'string' && rawStatus.toLowerCase().includes('accepted')) ||
            (typeof rawStatus === 'string' && rawStatus.toLowerCase().includes('confirmed')) ||
            (typeof rawStatus === 'string' && rawStatus.toLowerCase().includes('scanned'))
        );

        const declined = Boolean(
            rawStatus === 2 ||
            rawStatus === '2' ||
            invitationData?.declined === true ||
            invitationData?.isDeclined === true ||
            (typeof rawStatus === 'string' && rawStatus.toLowerCase().includes('declined')) ||
            (typeof rawStatus === 'string' && rawStatus.toLowerCase().includes('rejected'))
        );

        console.log('[RFW Invite] Status check - accepted:', accepted, 'declined:', declined);
        console.log('[RFW Invite] URL action param:', action);

        // RULE 1: If invitation has been accepted -> ALWAYS show invitation page (status is final)
        // Server is the source of truth - if backend says accepted, we show invitation regardless of URL params
        if (accepted) {
            console.log('[RFW Invite] ✓ Server status: ACCEPTED → showing invitation page');
            if (action && action !== 'accept') {
                console.log('[RFW Invite] ⚠️ URL has action param but status is already accepted - clearing URL');
                navigate(`/rfw-invite/${id}`, { replace: true });
            }
            setPageState('invitation');
            // trigger QR fetching when accepted and we have invitationData
            fetchEventQrcode();
            return;
        }

        // RULE 2: If invitation has been declined -> ALWAYS show thank you page (status is final)
        // Server is the source of truth - if backend says declined, we show thank you regardless of URL params
        if (declined) {
            console.log('[RFW Invite] ✓ Server status: DECLINED → showing thank you page');
            if (action && action !== 'decline') {
                console.log('[RFW Invite] ⚠️ URL has action param but status is already declined - clearing URL');
                navigate(`/rfw-invite/${id}`, { replace: true });
            }
            setPageState('thankyou');
            return;
        }

        // RULE 3: If action=accept and status is pending -> show policy page
        if (action === 'accept') {
            console.log('[RFW Invite] ✓ URL action=accept and status is pending → showing policy page');
            setPageState('policy');
            return;
        }

        // RULE 4: If action=decline and status is pending -> auto-decline
        if (action === 'decline') {
            console.log('[RFW Invite] ✓ URL action=decline and status is pending → auto-declining');
            handleDeclineInvitation();
            return;
        }

        // DEFAULT: Show accept state (no action param, not accepted/declined)
        console.log('[RFW Invite] ✓ Default state → showing accept page');
        setPageState('accept');
        // do not fetch QR unless accepted

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, error, invitationData, action, fetchEventQrcode]);

    // ----- Presentational helpers (view-only, no side-effects) -----
    const DetailRow: React.FC<{ iconSrc: string; label: string; value: string; corners?: string; onClick?: () => void; ltr?: boolean }> = ({ iconSrc, label, value, corners = '18px', onClick, ltr = false }) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                width: '100%',
                backgroundColor: 'rgba(37,37,37,0.15)',
                border: '1px solid rgba(33,33,33, 0.5)',
                borderRadius: corners,
                px: { xs: 2, sm: 3 },
                py: { xs: 2.25, sm: 2.5 },
                color: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(11px)',
                WebkitBackdropFilter: 'blur(8px)',
                gap: { xs: 1, sm: 1.5 }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <Box component="img" src={iconSrc} alt="" sx={{ width: 15, height: 15 }} />
                <Typography sx={{ fontSize: { xs: 14, sm: 16 }, color: 'rgba(255,255,255,0.33)', whiteSpace: 'nowrap' }}>{label}</Typography>
            </Box>
            <Typography
                onClick={onClick}
                sx={{
                    fontSize: { xs: 14, sm: 16 },
                    fontWeight: 600,
                    color: '#FFFFFF',
                    wordBreak: 'break-word',
                    textAlign: lang === 'ar' ? 'right' : { xs: 'left', sm: 'right' },
                    width: { xs: '100%', sm: 'auto' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: { xs: 3, sm: 2 },
                    WebkitBoxOrient: 'vertical',
                    cursor: onClick ? 'pointer' : 'default',
                    direction: ltr ? 'ltr' : undefined, // Force LTR for dates/times
                    '&:hover': onClick ? {
                        color: 'rgba(255,255,255,0.8)',
                        textDecoration: 'underline'
                    } : {}
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    // Page-scoped system UI color (iOS Safari notch/address bar) and backdrop
    useEffect(() => {
        const pageColor = '#0A0A0A';

        const previousBodyBg = document.body.style.backgroundColor;
        const previousHtmlBg = document.documentElement.style.backgroundColor;
        const previousColorScheme = (document.documentElement.style as CSSStyleDeclaration & { colorScheme?: string }).colorScheme || '';

        let themeMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
        const previousThemeColor = themeMeta?.getAttribute('content') || '';
        let createdMeta = false;

        if (!themeMeta) {
            themeMeta = document.createElement('meta');
            themeMeta.setAttribute('name', 'theme-color');
            document.head.appendChild(themeMeta);
            createdMeta = true;
        }
        themeMeta.setAttribute('content', pageColor);

        document.body.style.backgroundColor = pageColor;
        document.documentElement.style.backgroundColor = pageColor;
        (document.documentElement.style as CSSStyleDeclaration & { colorScheme?: string }).colorScheme = 'dark';

        return () => {
            document.body.style.backgroundColor = previousBodyBg;
            document.documentElement.style.backgroundColor = previousHtmlBg;
            (document.documentElement.style as CSSStyleDeclaration & { colorScheme?: string }).colorScheme = previousColorScheme;
            if (themeMeta) {
                if (createdMeta) {
                    document.head.removeChild(themeMeta);
                } else {
                    themeMeta.setAttribute('content', previousThemeColor || '#ffffff');
                }
            }
        };
    }, []);

    // Apply language; keep navbar/footer layout unchanged. Direction is set only on content container.
    useEffect(() => {
        const changeLanguageAsync = async () => {
            console.log('[RFW Invite] Changing language to:', lang);
            console.log('[RFW Invite] Current i18next language:', i18next.language);

            // Use i18next directly instead of the hook's i18n
            await i18next.changeLanguage(lang);

            try {
                localStorage.setItem('i18nextLng', lang);
            } catch { }

            console.log('[RFW Invite] Language changed, new i18next.language:', i18next.language);
            console.log('[RFW Invite] Has AR translations:', i18next.hasResourceBundle('ar', 'translation'));
            console.log('[RFW Invite] Has EN translations:', i18next.hasResourceBundle('en', 'translation'));

            // Force multiple re-renders to ensure translations update
            forceUpdate();
            setTimeout(() => forceUpdate(), 50);
            setTimeout(() => forceUpdate(), 200);
        };
        changeLanguageAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    // Sync with i18n language changes from other sources
    useEffect(() => {
        const handleLanguageChanged = (lng: string) => {
            console.log('[RFW Invite] i18n languageChanged event:', lng);
            const newLang = lng.startsWith('ar') ? 'ar' : 'en';
            if (newLang !== lang) {
                setLang(newLang);
            }
        };

        i18n.on('languageChanged', handleLanguageChanged);

        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, [i18n, lang]);

    // Load Nunito font for this page only
    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700&display=swap';
        document.head.appendChild(link);
        return () => {
            try { document.head.removeChild(link); } catch { }
        };
    }, []);

    const handleLangChange = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
        if (value) {
            console.log('========== LANGUAGE TOGGLE CLICKED ==========');
            console.log('[RFW Invite] User toggled language to:', value);
            console.log('[RFW Invite] useTranslation ready:', ready);
            console.log('[RFW Invite] Current i18n language BEFORE:', i18n.language);
            console.log('[RFW Invite] Current i18next language BEFORE:', i18next.language);
            console.log('[RFW Invite] Sample translations BEFORE:', {
                confirm: t('buttons.confirm'),
                dear: t('invitation.dear'),
                rfwTitle: t('invitation.rfw.title')
            });

            // Test direct i18next translation
            console.log('[RFW Invite] Direct i18next.t test:', {
                confirm: i18next.t('buttons.confirm'),
                dear: i18next.t('invitation.dear')
            });

            setLang(value);
            console.log('=============================================');
        }
    };

    /**
     * Handle Add to Wallet
     */
    const isMobileDevice = () => {
        const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
        return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    };

    const handleAddToWallet = async () => {
        const event0 = invitationData?.data?.event?.data?.[0];
        const contactId = invitationData?.data?.contact?.id;

        if (!event0?.event_id || !contactId) {
            alert('Missing event or contact info');
            return;
        }

        try {
            setWalletLoading(true);
            const res = await fetch(`${BASE_URL}/public/events/${event0.event_id}/wallet-pass`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer mazoom_guest_page_link`,
                },
                body: JSON.stringify({
                    contact_id: contactId,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }),
            });

            const data = await res.json();

            if (data.success && data.wallet_url) {
                if (isMobileDevice()) {
                    window.location.href = data.wallet_url;
                } else {
                    const walletWindow = window.open(data.wallet_url, '_blank');
                    if (!walletWindow || walletWindow.closed || typeof walletWindow.closed === 'undefined') {
                        window.location.href = data.wallet_url;
                    }
                }
            } else {
                alert(data.message || 'Failed to get wallet URL');
            }
        } catch (error) {
            alert('Network error or server unreachable');
        } finally {
            setWalletLoading(false);
        }
    };

    /**
     * Prepare calendar event data
     * Uses the same timezone logic as the display to ensure consistency
     */
    const getCalendarEvent = (): CalendarEvent => {
        const event0 = invitationData?.data?.event?.data?.[0];
        const startDatetime = event0?.start_datetime;

        // Convert UTC string to "local" date using the same logic as display
        const startLocal = startDatetime ? utcStringToLocalDate(startDatetime) : null;
        
        // Calculate end time (2 hours after start)
        const endLocal = startLocal 
            ? new Date(startLocal.getTime() + 2 * 60 * 60 * 1000)
            : null;

        // Format as ISO string WITHOUT 'Z' marker so calendar apps treat as local time
        // Format: "YYYY-MM-DDTHH:mm:ss"
        const formatLocalISO = (date: Date | null): string => {
            if (!date || isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        };

        return {
            title: event0?.name || 'Riyadh Fashion Week',
            description: '',
            start: formatLocalISO(startLocal),
            end: formatLocalISO(endLocal),
            location: viewModel?.locationStr || `${event0?.venue}, ${event0?.address?.address}`,
        };
    };

    /**
     * Handle Accept Invitation (Single Responsibility: handle accept action)
     * Following Open/Closed: Can extend without modifying existing code
     */
    const handleAcceptInvitation = async () => {
        if (!id || submissionState !== 'idle') return;

        try {
            setSubmissionState('accepting');
            console.log('[RFW Invite] Accepting invitation with hash:', id);

            const response = await invitationService.acceptInvitation(id, 0);
            console.log('[RFW Invite] Accept response:', response);

            // Re-fetch invitation data from server to get updated status
            // This ensures we have the latest state and prevents stale data issues
            await fetchInvitationData();

            // Clear URL params so state derivation can take over based on fresh server data
            navigate(`/rfw-invite/${id}`, { replace: true });
        } catch (error: any) {
            console.error('[RFW Invite] Error accepting invitation:', error);
            // Check if error is because already responded
            if (error.message?.toLowerCase().includes('already') || 
                error.response?.status === 409 || 
                error.response?.status === 422) {
                // Re-fetch to sync with server state
                await fetchInvitationData();
                navigate(`/rfw-invite/${id}`, { replace: true });
            } else {
                alert(error.message || 'Failed to accept invitation. Please try again.');
            }
        } finally {
            setSubmissionState('idle');
        }
    };

    /**
     * Handle Decline Invitation (Single Responsibility: handle decline action)
     */
    const handleDeclineInvitation = async () => {
        if (!id || submissionState !== 'idle') return;

        try {
            setSubmissionState('declining');
            console.log('[RFW Invite] Declining invitation with hash:', id);

            const response = await invitationService.declineInvitation(id);
            console.log('[RFW Invite] Decline response:', response);

            // Re-fetch invitation data from server to get updated status
            // This ensures we have the latest state and prevents stale data issues
            await fetchInvitationData();

            // Clear URL params so state derivation can take over based on fresh server data
            navigate(`/rfw-invite/${id}`, { replace: true });
        } catch (error: any) {
            console.error('[RFW Invite] Error declining invitation:', error);
            // Check if error is because already responded
            if (error.message?.toLowerCase().includes('already') || 
                error.response?.status === 409 || 
                error.response?.status === 422) {
                // Re-fetch to sync with server state
                await fetchInvitationData();
                navigate(`/rfw-invite/${id}`, { replace: true });
            } else {
                alert(error.message || 'Failed to decline invitation. Please try again.');
            }
        } finally {
            setSubmissionState('idle');
        }
    };

    // Key to force remount on language change
    const componentKey = `rfw-invite-${lang}-${i18n.language}`;

    // Debug: Log every render
    console.log('[RFW Invite RENDER]', {
        lang,
        i18nLanguage: i18n.language,
        i18nextLanguage: i18next.language,
        ready,
        sampleTranslation: t('buttons.confirm'),
        directTranslation: i18next.t('buttons.confirm')
    });

    return (
        <div
            key={componentKey}
            style={{
                position: 'relative',
                minHeight: '100vh',
                fontFamily: 'Nunito, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
            }}
        >
            {/* Fixed Background Layer */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundImage: 'url(/images/rfw-bg.jpeg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    zIndex: 0,
                    pointerEvents: 'none'
                }}
            />

            {/* Dark Glass Navbar */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    minHeight: 'auto',
                    backgroundColor: 'rgba(37,37,37,0.15)',
                    backdropFilter: 'blur(8px)',
                    borderBottom: '1px solid rgba(33,33,33,1.0)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: { xs: '16px', md: '24px' },
                    paddingRight: { xs: '16px', md: '24px' },
                    paddingTop: { xs: '16px', md: '20px' },
                    paddingBottom: { xs: '16px', md: '20px' },
                    boxSizing: 'border-box',
                    zIndex: 2
                }}
            >
                {/* Navbar Content */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%'
                    }}
                >
                    <Box
                        component="img"
                        src="/images/logo-rfw.svg"
                        alt="RFW Logo"
                        sx={{
                            height: { xs: '40px', md: '50px' },
                            width: 'auto'
                        }}
                    />
                    <ToggleButtonGroup
                        size="small"
                        color="standard"
                        value={lang}
                        exclusive
                        onChange={handleLangChange}
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            borderRadius: '9999px',
                            overflow: 'hidden',
                            p: '2px',
                            border: '1px solid rgba(255,255,255,0.15)',
                            '& .MuiToggleButtonGroup-grouped': {
                                margin: 0,
                                border: 0,
                                borderRadius: '9999px',
                            },
                            '& .MuiToggleButton-root': {
                                color: 'white',
                                px: 1.5,
                                py: 0.25,
                                fontSize: { xs: '12px', md: '13px' },
                                border: 'none',
                                borderRadius: '9999px',
                                '&.Mui-selected': {
                                    backgroundColor: '#ffffff',
                                    color: '#000000',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.12)'
                                }
                            }
                        }}
                    >
                        <ToggleButton value="en">EN</ToggleButton>
                        <ToggleButton value="ar">AR</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* Scrollable Content Layer */
                // Page content renderer respects the current page state
            }
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    minHeight: { xs: '87svh', md: '87vh' },
                    display: 'flex',
                    alignItems: (pageState === 'error' || pageState === 'thankyou') ? 'center' : { xs: 'flex-start', md: 'flex-start' },
                    justifyContent: 'center',
                    pt: (pageState === 'error' || pageState === 'thankyou') ? 0 : { xs: 'calc(env(safe-area-inset-top, 0px) + 8.4vh)', md: 'calc(env(safe-area-inset-top, 0px) + 9vh)' },
                    px: { xs: '9px', md: '24px' },
                    pb: 0,
                    boxSizing: 'border-box'
                }}
                dir={lang === 'ar' ? 'rtl' : 'ltr'}
            >
                {pageState === 'loading' && (
                    <Paper
                        elevation={0}
                        sx={{
                            px: 4,
                            py: { xs: 2.5, md: 4 },
                            textAlign: 'center',
                            borderRadius: 4,
                            maxWidth: 500,
                            width: '100%',
                            backgroundColor: 'transparent',
                            backdropFilter: 'none',
                            border: 'none',
                            boxShadow: 'none',
                            color: 'white',
                            '@media (min-width: 900px)': {
                                backgroundColor: 'rgba(255, 255, 255, 0.005)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                color: 'white',
                                fontWeight: '300',
                                marginBottom: '24px',
                                textAlign: 'center',
                                fontSize: { xs: '18px', md: '24px' }
                            }}
                        >
                            {isLoading ? t('invitation.loadingInvitation') :
                                error ? t('invitation.notFound') :
                                    invitationData ? t('invitation.welcome') :
                                        t('invitation.loadingInvitation')}
                        </Typography>
                        {isLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, mt: 0 }}>
                                <div
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        border: '2px solid rgba(255, 255, 255, 0.3)',
                                        borderTop: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}
                                />
                            </Box>
                        )}
                        <style>
                            {`
                                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    `}
                        </style>
                    </Paper>
                )}

                {pageState === 'accept' && (
                    <Paper
                        elevation={0}
                        sx={{
                            px: { xs: 2.5, md: 4 },
                            py: { xs: 3, md: 4 },
                            textAlign: 'center',
                            borderRadius: 4,
                            width: '100%',
                            maxWidth: { xs: 680, md: 880 },
                            backgroundColor: 'transparent',
                            color: 'white'
                        }}
                    >
                        {/* Title Block */}
                        <Typography sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 1.2,
                            fontSize: { xs: 12, md: 14 },
                            mb: 2,
                            opacity: 0.9,
                            textAlign: lang === 'ar' ? 'right' : 'left',
                            maxWidth: 760,
                            mx: 'auto'
                        }}>
                            {(viewModel?.guestName || 'Guest') + ' ' + t('invitation.invitedToAttend', "you're invited too")}
                        </Typography>

                        <Typography sx={{
                            fontFamily: 'serif',
                            maxWidth: 760,
                            fontWeight: 700,
                            fontSize: { xs: 40, md: 48 },
                            lineHeight: 1.2,
                            // whiteSpace: 'pre-line',
                            textAlign: lang === 'ar' ? 'right' : 'left',
                            mx: 'auto',
                            mb: { xs: 1.25, md: 1.5 }
                        }}>
                            {getEventTitle(invitationData?.data?.event?.data?.[0], lang)}
                        </Typography>

                        {/* Dynamic description text - show only if available from API */}
                        {(() => {
                            const event0 = invitationData?.data?.event?.data?.[0];
                            const description = lang === 'ar' ? event0?.description_ar : event0?.description_en;

                            // Only render if description exists and is not null/empty
                            if (description) {
                                // Sanitize HTML to prevent XSS attacks
                                const sanitizedDescription = DOMPurify.sanitize(description, {
                                    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span', 'div'],
                                    ALLOWED_ATTR: []
                                });

                                return (
                                    <Box
                                        sx={{
                                            fontSize: { xs: 16, md: 16 },
                                            color: 'white',
                                            opacity: .9,
                                            maxWidth: 760,
                                            mx: 'auto',
                                            mb: { xs: 3, md: 4 },
                                            lineHeight: 1.6,
                                            textAlign: lang === 'ar' ? 'right' : 'left',
                                            '& b, & strong': {
                                                fontWeight: 700,
                                                opacity: 1
                                            },
                                            '& br': {
                                                display: 'block',
                                                content: '""',
                                                marginTop: '0.5em'
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                                    />
                                );
                            }
                            return null;
                        })()}

                        {/* Glass Details Panel */}
                        <Box sx={{ display: 'grid', gap: '5px', mb: { xs: 3, md: 3 }, maxWidth: 760, mx: 'auto' }}>
                            <DetailRow
                                iconSrc={'/images/icon-calendar.svg'}
                                label={`${t('invitation.rfw.date', 'Date')}`}
                                value={viewModel?.dateStr || ''}
                                corners={'18px 18px 3px 3px'}
                                ltr={true}
                            />
                            <DetailRow
                                iconSrc={'/images/icon-location.svg'}
                                label={`${t('invitation.rfw.location', 'Location')}`}
                                value={viewModel?.locationStr || ''}
                                corners={'3px'}
                                onClick={() => {
                                    // Get event data to extract coordinates
                                    const event0 = invitationData?.data?.event?.data?.[0];
                                    const url = buildMapsUrl(
                                        event0?.venue,
                                        event0?.address?.address,
                                        event0?.address?.lat,
                                        event0?.address?.lng
                                    );
                                    if (url) {
                                        window.open(url, '_blank');
                                    }
                                }}
                            />
                            <DetailRow
                                iconSrc={'/images/icon-clock.svg'}
                                label={`${t('invitation.rfw.time', 'Time')}`}
                                value={viewModel?.timeStr || ''}
                                corners={'3px 3px 18px 18px'}
                                ltr={true}
                            />
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: .5, justifyContent: 'center', mb: { xs: 7, md: 3 } }}>
                            <Box
                                onClick={() => navigate(`/rfw-invite/${id}?action=accept`)}
                                sx={{
                                    cursor: 'pointer',
                                    px: { xs: 7, md: 7 },
                                    py: { xs: 1.75, md: 1.75 },
                                    borderRadius: lang === 'ar' ? '3px 28px 28px 3px' : '28px 3px 3px 28px',
                                    backgroundColor: '#ffffff',
                                    color: '#000',
                                    fontWeight: 700,
                                    fontSize: { xs: '18px', md: '18px' },
                                    letterSpacing: 0.5,
                                    border: '1px solid rgba(255,255,255,0.35)'
                                }}
                            >
                                {t('buttons.confirm', 'Confirm')}
                            </Box>
                            <Box
                                onClick={() => navigate(`/rfw-invite/${id}?action=decline`)}
                                sx={{
                                    cursor: 'pointer',
                                    px: { xs: 7, md: 7 },
                                    py: { xs: 1.75, md: 1.75 },
                                    borderRadius: lang === 'ar' ? '28px 3px 3px 28px' : '3px 28px 28px 3px',
                                    border: '1.5px solid #FFFFFF',
                                    backgroundColor: 'rgba(37,37,37,0.15)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(9px)',
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: { xs: '18px', md: '18px' },
                                    letterSpacing: 0.5
                                }}
                            >
                                {t('buttons.decline', 'Decline')}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {pageState === 'policy' && (
                    <Paper
                        elevation={0}
                        sx={{
                            px: { xs: 2.5, md: 4 },
                            py: { xs: 3, md: 4 },
                            textAlign: lang === 'ar' ? 'right' : 'left',
                            borderRadius: 4,
                            width: '100%',
                            maxWidth: { xs: 760, md: 900 },
                            backgroundColor: 'transparent',
                            color: 'white'
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'serif',
                                fontWeight: 700,
                                fontSize: { xs: 28, md: 36 },
                                textAlign: lang === 'ar' ? 'right' : 'left',
                                mb: { xs: 2.5, md: 3 }
                            }}
                        >
                            {t('invitation.policy.title', 'Guest Policy')}
                        </Typography>

                        {/* Dynamic policy content from API, fallback to hardcoded translations */}
                        {(() => {
                            const event0 = invitationData?.data?.event?.data?.[0];
                            const policyText = lang === 'ar' ? event0?.policy_ar : event0?.policy_en;

                            // If policy text exists from API, show it
                            if (policyText) {
                                // Sanitize HTML to prevent XSS attacks
                                const sanitizedPolicy = DOMPurify.sanitize(policyText, {
                                    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span', 'div', 'ul', 'ol', 'li'],
                                    ALLOWED_ATTR: []
                                });

                                return (
                                    <Box
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.85)',
                                            fontSize: { xs: 14, md: 16 },
                                            lineHeight: 1.6,
                                            mb: { xs: 2.5, md: 3 },
                                            '& b, & strong': {
                                                fontWeight: 700,
                                                color: 'rgba(255, 255, 255, 1)',
                                                fontSize: { xs: 16, md: 18 }
                                            },
                                            '& br': {
                                                display: 'block',
                                                content: '""',
                                                marginTop: '0.5em'
                                            },
                                            '& p': {
                                                margin: '0 0 1em 0'
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: sanitizedPolicy }}
                                    />
                                );
                            }

                            // Fallback to hardcoded translations
                            return (
                                <>
                                    <Box sx={{ mb: { xs: 2.5, md: 3 }, lineHeight: 1.6 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, md: 20 }, mb: 1 }}>{t('invitation.policy.items.dressCode.title', 'Dress Code')}</Typography>
                                        <Typography sx={{ opacity: 0.85, fontSize: { xs: 14, md: 16 } }}>{t('invitation.policy.items.dressCode.body')}</Typography>
                                    </Box>
                                    <Box sx={{ mb: { xs: 2.5, md: 3 }, lineHeight: 1.6 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, md: 20 }, mb: 1 }}>{t('invitation.policy.items.mediaConsent.title', 'Media Consent')}</Typography>
                                        <Typography sx={{ opacity: 0.85, fontSize: { xs: 14, md: 16 } }}>{t('invitation.policy.items.mediaConsent.body')}</Typography>
                                    </Box>
                                    <Box sx={{ mb: { xs: 2.5, md: 3 }, lineHeight: 1.6 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, md: 20 }, mb: 1 }}>{t('invitation.policy.items.nonTransferable.title', 'Non-Transferable Invitations')}</Typography>
                                        <Typography sx={{ opacity: 0.85, fontSize: { xs: 14, md: 16 } }}>{t('invitation.policy.items.nonTransferable.body')}</Typography>
                                    </Box>
                                    <Box sx={{ mb: { xs: 2.5, md: 3 }, lineHeight: 1.6 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, md: 20 }, mb: 1 }}>{t('invitation.policy.items.rightOfAdmission.title', 'Right of Admission')}</Typography>
                                        <Typography sx={{ opacity: 0.85, fontSize: { xs: 14, md: 16 } }}>{t('invitation.policy.items.rightOfAdmission.body')}</Typography>
                                    </Box>
                                </>
                            );
                        })()}

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: .5, justifyContent: 'center', mt: { xs: 3, md: 4 }, mb: { xs: 7, md: 3 } }}>
                            <Box
                                onClick={handleAcceptInvitation}
                                sx={{
                                    cursor: submissionState === 'idle' ? 'pointer' : 'not-allowed',
                                    px: { xs: 7, md: 7 },
                                    py: { xs: 1.75, md: 1.75 },
                                    borderRadius: lang === 'ar' ? '3px 28px 28px 3px' : '28px 3px 3px 28px',
                                    backgroundColor: '#ffffff',
                                    color: '#000',
                                    fontWeight: 700,
                                    fontSize: { xs: '18px', md: '18px' },
                                    letterSpacing: 0.5,
                                    border: '1px solid rgba(255,255,255,0.35)',
                                    opacity: submissionState === 'declining' ? 0.5 : 1,
                                    pointerEvents: submissionState === 'declining' ? 'none' : 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1
                                }}
                            >
                                {submissionState === 'accepting' ? (
                                    <CircularProgress size={20} sx={{ color: '#000' }} />
                                ) : (
                                    t('buttons.accept', 'Accept')
                                )}
                            </Box>
                            <Box
                                onClick={handleDeclineInvitation}
                                sx={{
                                    cursor: submissionState === 'idle' ? 'pointer' : 'not-allowed',
                                    px: { xs: 7, md: 7 },
                                    py: { xs: 1.75, md: 1.75 },
                                    borderRadius: lang === 'ar' ? '28px 3px 3px 28px' : '3px 28px 28px 3px',
                                    border: '1.5px solid #FFFFFF',
                                    backgroundColor: 'rgba(37,37,37,0.15)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: { xs: '18px', md: '18px' },
                                    letterSpacing: 0.5,
                                    opacity: submissionState === 'accepting' ? 0.5 : 1,
                                    pointerEvents: submissionState === 'accepting' ? 'none' : 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1
                                }}
                            >
                                {submissionState === 'declining' ? (
                                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                                ) : (
                                    t('buttons.decline', 'Reject')
                                )}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {pageState === 'invitation' && (
                    <Paper
                        elevation={0}
                        sx={{
                            px: { xs: 2.5, md: 3 },
                            py: { xs: 3, md: 4 },
                            textAlign: 'center',
                            borderRadius: 4,
                            width: '100%',
                            maxWidth: { xs: 420, md: 480 },
                            backgroundColor: 'transparent',
                            color: 'white'
                        }}
                    >
                        {/* Status and Zone Badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, direction: 'ltr' }}>
                            <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('invitation.confirmed', 'CONFIRMED')}
                            </Typography>
                            <Box
                                sx={{
                                    px: 2,
                                    py: .5,
                                    backgroundColor: 'white',
                                    color: 'black',
                                    borderRadius: '999px',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5
                                }}
                            >
                                {invitationData?.data?.zone ? `ZONE ${invitationData.data.zone}` : 'ZONE 1'}
                            </Box>
                        </Box>

                        {/* Guest Name */}
                        <Typography
                            sx={{
                                fontFamily: 'serif',
                                fontSize: 28,
                                fontWeight: 700,
                                textAlign: 'left',
                                letterSpacing: 0.5
                            }}
                        >
                            {viewModel?.guestName ? viewModel.guestName.toUpperCase() : 'GUEST'}
                        </Typography>

                        {/* Date/Time */}
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: 'rgba(255,255,255,0.6)',
                                mb: 0.5,
                                textAlign: 'left',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                direction: 'ltr'
                            }}
                        >
                            {viewModel?.dateStr || 'OCTOBER 2, 2025'} - {viewModel?.timeStr || '02:00 AM'}
                        </Typography>

                        {/* Location */}
                        {viewModel?.locationStr && (
                            <Box
                                onClick={() => {
                                    // Get event data to extract coordinates
                                    const event0 = invitationData?.data?.event?.data?.[0];
                                    const url = buildMapsUrl(
                                        event0?.venue,
                                        event0?.address?.address,
                                        event0?.address?.lat,
                                        event0?.address?.lng
                                    );
                                    if (url) {
                                        window.open(url, '_blank');
                                    }
                                }}
                                sx={{
                                    fontSize: 13,
                                    color: 'rgba(255,255,255,0.6)',
                                    mb: 3,
                                    letterSpacing: 0.5,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    transition: 'color 0.2s',
                                    width: 'fit-content',
                                    direction: 'ltr',
                                    textAlign: 'left',
                                    marginLeft: 0,
                                    marginRight: 'auto',
                                    '&:hover': {
                                        color: 'rgba(255,255,255,0.9)'
                                    }
                                }}
                            >
                                <Box
                                    component="svg"
                                    sx={{ width: 14, height: 14, opacity: 0.7, flexShrink: 0 }}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                >
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" />
                                    <circle cx="12" cy="10" r="3" strokeWidth="2" />
                                </Box>
                                <span>{viewModel.locationStr}</span>
                            </Box>
                        )}

                        {/* QR Code (server-generated, same as Invitation page) */}
                        <Box sx={{ mb: 4.9 }}>
                            {/* White Square - QR Code Container */}
                            <Box
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: '24px 24px 0 0',
                                    aspectRatio: '1 / 1',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    p: 2
                                }}
                            >
                                {qrcodeLoading ? (
                                    <CircularProgress size={28} />
                                ) : eventQrcode ? (
                                    <Box
                                        component="img"
                                        src={`data:image/png;base64,${eventQrcode}`}
                                        alt="Event QR Code"
                                        sx={{
                                            width: '90%',
                                            height: '90%',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                    />
                                ) : (
                                    <Typography color="text.secondary">{t('invitation.qrNotAvailable', 'QR code not available.')}</Typography>
                                )}
                            </Box>

                            {/* Black Square - Event Title Container */}
                            {eventQrcode && (
                                <Box
                                    sx={{
                                        backgroundColor: 'black',
                                        borderRadius: '0 0 24px 24px',
                                        py: { xs: 3, md: 3 },
                                        px: { xs: 2.5, md: 3 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'serif',
                                            fontSize: { xs: 14, md: 15 },
                                            fontWeight: 700,
                                            color: 'white',
                                            textAlign: 'center',
                                            letterSpacing: { xs: 1, md: 1.2 },
                                            textTransform: 'uppercase',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {getEventTitle(invitationData?.data?.event?.data?.[0], lang)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Instructions */}
                        <Box sx={{ mb: 2.5 }}>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 600,
                                    mb: 1,
                                    textAlign: 'center'
                                }}
                            >
                                {t('invitation.howToUse', 'How to use your QR code')}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: 'rgba(255,255,255,0.7)',
                                    lineHeight: 1.5,
                                    textAlign: 'center'
                                }}
                            >
                                {t('invitation.qrInstructions', 'Save this QR code to your phone or print it. Present it at the event entrance for check-in.')}
                            </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box
                                onClick={handleAddToWallet}
                                sx={{
                                    cursor: walletLoading ? 'not-allowed' : 'pointer',
                                    py: 1.75,
                                    borderRadius: '999px',
                                    backgroundColor: 'white',
                                    color: 'black',
                                    fontWeight: 700,
                                    fontSize: 16,
                                    textAlign: 'center',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    transition: 'opacity 0.2s',
                                    opacity: walletLoading ? 0.7 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    '&:hover': {
                                        opacity: walletLoading ? 0.7 : 0.9
                                    }
                                }}
                            >
                                {walletLoading ? (
                                    <>
                                        <CircularProgress size={18} sx={{ color: '#000' }} />
                                        <span>{t('invitation.loadingWalletPass', 'Loading...')}</span>
                                    </>
                                ) : (
                                    t('invitation.addToWallet', 'ADD TO WALLET')
                                )}
                            </Box>
                            <Box
                                onClick={() => setCalendarModalOpen(true)}
                                sx={{
                                    cursor: 'pointer',
                                    py: 1.75,
                                    borderRadius: '999px',
                                    backgroundColor: 'transparent',
                                    border: '2px solid white',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: 16,
                                    textAlign: 'center',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                {t('invitation.addToCalendar', 'ADD TO CALENDAR')}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {pageState === 'error' && (
                    <Paper
                        elevation={0}
                        sx={{
                            px: { xs: 3, md: 4 },
                            py: { xs: 4, md: 5 },
                            textAlign: 'center',
                            borderRadius: 4,
                            width: '100%',
                            maxWidth: { xs: 420, md: 480 },
                            backgroundColor: 'transparent',
                            color: 'white',
                            fontFamily: 'Nunito, sans-serif'
                        }}
                    >
                        {/* Error Icon */}
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}
                        >
                            <Box
                                component="svg"
                                sx={{ width: 32, height: 32 }}
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
                                <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            </Box>
                        </Box>

                        {/* Error Title */}
                        <Typography
                            sx={{
                                fontFamily: 'serif',
                                fontSize: { xs: 24, md: 28 },
                                fontWeight: 700,
                                mb: 2,
                                lineHeight: 1.3
                            }}
                        >
                            {t('invitation.error.invalidTitle', 'Invalid Invitation')}
                        </Typography>

                        {/* Error Message */}
                        <Typography
                            sx={{
                                fontSize: { xs: 14, md: 16 },
                                color: 'rgba(255,255,255,0.7)',
                                lineHeight: 1.6
                            }}
                        >
                            {t('invitation.error.invalidMessage', 'This invitation link is not valid or may have expired. Please check your invitation or contact our support team for assistance.')}
                        </Typography>
                    </Paper>
                )}

                {pageState === 'thankyou' && (
                    <Paper
                        elevation={0}
                        sx={{
                            px: { xs: 3, md: 4 },
                            py: { xs: 4, md: 5 },
                            textAlign: 'center',
                            borderRadius: 4,
                            width: '100%',
                            maxWidth: { xs: 420, md: 480 },
                            backgroundColor: 'transparent',
                            color: 'white',
                            fontFamily: 'Nunito, sans-serif'
                        }}
                    >
                        {/* Thank You Icon */}
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}
                        >
                            <Box
                                component="svg"
                                sx={{ width: 32, height: 32 }}
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </Box>
                        </Box>

                        {/* Thank You Title */}
                        <Typography
                            sx={{
                                fontFamily: 'serif',
                                fontSize: { xs: 24, md: 28 },
                                fontWeight: 700,
                                mb: 2,
                                lineHeight: 1.3
                            }}
                        >
                            {t('invitation.thankYou.title', 'Thank you')}
                        </Typography>

                        {/* Thank You Message */}
                        <Typography
                            sx={{
                                fontSize: { xs: 14, md: 16 },
                                color: 'rgba(255,255,255,0.7)',
                                lineHeight: 1.6,
                                mb: 2
                            }}
                        >
                            {t('invitation.thankYou.message', "We look forward to hosting you at future editions of Riyadh Fashion Week.")}
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* Dark Glass Footer */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    // mt: { xs: '50px', md: '50px' },
                    minHeight: 'auto',
                    backgroundColor: 'rgba(37,37,37,0.15)',
                    backdropFilter: 'blur(8px)',
                    borderTop: '1px solid rgba(101, 101, 101, 0.3)',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: { xs: 'center', md: 'space-between' },
                    paddingLeft: { xs: '16px', md: '24px' },
                    paddingRight: { xs: '16px', md: '24px' },
                    paddingTop: { xs: '24px', md: '32px' },
                    paddingBottom: { xs: '24px', md: '32px' },
                    gap: { xs: '20px', md: '0px' },
                    boxSizing: 'border-box'
                }}
            >
                {/* Social Media Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'center', md: 'flex-start' },
                        justifyContent: 'space-between',
                        height: { xs: 'auto', md: '100%' },
                        gap: { xs: '12px', md: '8px' }
                    }}
                >
                    {/* Social Media Icons */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: '12px', md: '6px' },
                            justifyContent: { xs: 'center', md: 'flex-start' },
                            flex: { xs: 'none', md: '1' }
                        }}
                    >
                        {[
                            { src: '/images/ig_logo.svg', alt: 'Instagram', href: 'https://www.instagram.com/riyadhfashionweek/' },
                            { src: '/images/x_logo.svg', alt: 'X (Twitter)', href: 'https://x.com/riyadhfw' }
                        ].map((social) => (
                            <Box
                                key={social.alt}
                                component="a"
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s ease',
                                    '&:hover': { opacity: '0.7' }
                                }}
                            >
                                <Box
                                    component="img"
                                    src={social.src}
                                    alt={social.alt}
                                    sx={{
                                        height: { xs: '28px', md: '24px' },
                                        width: 'auto'
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Website Link */}
                    <Box
                        component="a"
                        href="https://www.riyadhfashionweek.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'white',
                            textDecoration: 'none',
                            fontSize: { xs: '16px', md: '16px' },
                            fontWeight: '300',
                            opacity: '0.8',
                            transition: 'opacity 0.2s ease',
                            textAlign: { xs: 'center', md: 'left' },
                            alignSelf: { xs: 'center', md: 'flex-end' },
                            '&:hover': { opacity: '1' }
                        }}
                    >
                        www.riyadhfashionweek.com
                    </Box>
                </Box>

                {/* Partner Logos Section */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: '8px', md: '24px' },
                        flexDirection: 'row',
                        px: { xs: 0, md: 0 }
                    }}
                >
                    {/* Saudia */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', md: 'flex-end' },
                            justifyContent: 'center',
                            gap: { xs: '5px', md: '8px' }
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/logo-saudia.png"
                            alt="Saudia"
                            sx={{
                                height: { xs: '35px', md: '45px' },
                                width: 'auto'
                            }}
                        />
                        <Box
                            sx={{
                                color: 'white',
                                fontSize: { xs: '9px', md: '12px' },
                                fontWeight: '400',
                                opacity: '0.8',
                                textAlign: 'center',
                                letterSpacing: { xs: '0.3px', md: '0.5px' },
                                whiteSpace: { xs: 'nowrap', md: 'normal' },
                                lineHeight: 1.2
                            }}
                        >
                            OFFICIAL AIRLINE PARTNER
                        </Box>
                    </Box>

                    {/* Divider */}
                    <Box
                        sx={{
                            height: { xs: '55px', md: '80px' },
                            width: '1px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            flexShrink: 0
                        }}
                    />

                    {/* Westfield */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', md: 'flex-end' },
                            justifyContent: 'center',
                            gap: { xs: '5px', md: '8px' }
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/logo-westfield.png"
                            alt="Westfield"
                            sx={{
                                height: { xs: '35px', md: '45px' },
                                width: 'auto'
                            }}
                        />
                        <Box
                            sx={{
                                color: 'white',
                                fontSize: { xs: '9px', md: '12px' },
                                fontWeight: '400',
                                opacity: '0.8',
                                textAlign: 'center',
                                letterSpacing: { xs: '0.3px', md: '0.5px' },
                                whiteSpace: { xs: 'nowrap', md: 'normal' },
                                lineHeight: 1.2
                            }}
                        >
                            OFFICIAL PARTNER
                        </Box>
                    </Box>
                    {/* Divider */}
                    <Box
                        sx={{
                            height: { xs: '55px', md: '80px' },
                            width: '1px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            flexShrink: 0
                        }}
                    />
                    {/* BSF */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: { xs: '5px', md: '8px' },
                            minWidth: { xs: 'auto', md: '100px' }
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: { xs: '32px', md: '45px' }
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/logo-bsf.png"
                                alt="BSF"
                                sx={{
                                    height: { xs: '12px', md: '28px' },
                                    width: 'auto',
                                    // objectFit: 'contain'
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                color: 'white',
                                fontSize: { xs: '9px', md: '12px' },
                                fontWeight: '400',
                                opacity: '0.8',
                                textAlign: 'center',
                                letterSpacing: { xs: '0.3px', md: '0.5px' },
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2
                            }}
                        >
                            BANKING PARTNER
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Calendar Modal */}
            <CalendarModal
                open={calendarModalOpen}
                onClose={() => setCalendarModalOpen(false)}
                event={getCalendarEvent()}
                eventhash={invitationData?.data?.hash}
            />
        </div>
    );
};

export default RfwInvite;